'use client';

import { useCallback, useState } from 'react';

import { FlowChannels, emit } from '@ps/features/flow-registry/flow-events';
import { postBioEntityInvalidation } from '@ps/functions/bio/postBioEntityInvalidation';
import type { PopStoreExtendedSubscription } from '@ps/models/lib/creator-subscription';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@ps/ui/atoms/dialog';
import {
  useCurrentUserPrivateProfile,
  useCurrentUserProfile,
} from '@ps/ui/contexts/user-profile';
import { ChevronLeft } from 'lucide-react';

import { useFetchAllSubscriptions } from '../../hooks/useFetchAllSubscriptions';
import { useTierItems } from '../../hooks/useTierItems';
import { AgeVerificationCheckbox } from '../AgeVerificationCheckbox';
import { SubscriptionTierRow } from '../SubscriptionTierRow';

type SubscriptionTierLevel = PopStoreExtendedSubscription['level'];

/**
 * Manage Subscription modal — opens from the Home page's Quick
 * Settings card. 1:1 with legacy
 * `flows/bio/pages/subscription-configuration` (page title:
 * "Configure Subscriptions").
 *
 * Turn 3: per-tier setup field fully wired.
 *   - Price + optional VIP discount + description textarea
 *     + banner upload + save button live inside each row.
 *   - Save dispatches: FOLLOWER → firestore merge of
 *     `followersSettings`; paid tier → `setupSubscriptionConfig`
 *     (new) or `updateSubscriptionConfig` (existing).
 *   - After a successful save we call `refresh()` so the list
 *     repaints with the new state, then surface an inline toast
 *     for ~3 seconds.
 *
 * The 18+ self-certification checkbox gates the paid tiers until certified (the
 * paid rows come back `disabled` from `useTierItems` while `isAgeVerified` is
 * false); it removes itself once done. The plan-gate rule
 * (`canConfigureSubscriptionLevel`) also runs inside `useTierItems`.
 */
export interface ManageSubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires after a successful save round-trip. Routes hook this
   *  to `router.back()` if they want the modal to dismiss after
   *  the first save. */
  onSaved?: () => void;
  /** Highlights the target tier's collapsed row (mint accent
   *  border) without auto-expanding it — the row stays collapsed
   *  so clicking it still fires the Stripe-charges gate. */
  defaultSubscriptionLevel?: SubscriptionTierLevel;
}

export function ManageSubscriptionModal({
  open,
  onOpenChange,
  onSaved,
  defaultSubscriptionLevel,
}: ManageSubscriptionModalProps) {
  const profile = useCurrentUserProfile();
  const privateProfile = useCurrentUserPrivateProfile();
  const creatorId = profile?.id;
  const isAgeVerified = privateProfile?.isAgeCertified ?? false;

  const { subscriptions, loading, error, refresh } =
    useFetchAllSubscriptions(creatorId);
  const items = useTierItems({ subscriptions, isAgeVerified });

  // Accordion semantics — only one tier expanded at a time.
  // Opening a tier collapses the others; closing the open tier
  // leaves all collapsed. 1:1 with legacy `openLevel` state in
  // `SubscriptionSetupContent`.
  const [openLevel, setOpenLevel] = useState<
    SubscriptionTierLevel | undefined
  >();

  const [toast, setToast] = useState<{ type: 'error'; msg: string }>();

  // Hosts routing this modal (route-intercept / editor-target
  // switch) keep it mounted with a literal `open` (never flips
  // to `false` on their end) rather than unmounting it, so
  // `openLevel` would otherwise survive a close+reopen. Reset it
  // at the actual close trigger instead of watching `open`.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) setOpenLevel(undefined);
      onOpenChange(next);
    },
    [onOpenChange],
  );

  const close = useCallback(() => handleOpenChange(false), [handleOpenChange]);

  const dismissToastAfter = useCallback((delayMs = 3000) => {
    window.setTimeout(() => setToast(undefined), delayMs);
  }, []);

  // Success is surfaced inline inside the saved tier's own card
  // (see `SubscriptionSetupField`'s `justSaved` message) rather
  // than as a modal-wide toast — it used to float over whatever
  // row happened to be open, reading as detached from the save
  // it was confirming.
  const handleTierSaved = useCallback(() => {
    void refresh();
    setOpenLevel(undefined);
    // Broadcast so every OTHER subscription consumer updates live —
    // the course form's access settings and the clip editor's tier
    // picker are mounted BEHIND this modal (route intercept) and
    // would otherwise keep their stale mount-time tier list.
    emit(FlowChannels.SubscriptionConfigSaved, {});
    // A tier was configured/enabled or disabled — bust the visitor bio's
    // subscription gate so the Subscribe / Message Me main action buttons
    // appear or hide (session-derived uid, so the owner busts only itself).
    postBioEntityInvalidation({ kind: 'subscriptions' });
    onSaved?.();
  }, [refresh, onSaved]);

  const handleTierError = useCallback(
    (msg: string) => {
      setToast({ type: 'error', msg });
      dismissToastAfter(5000);
    },
    [dismissToastAfter],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* `max-h`, not `h`. A FIXED height held the panel at 587-610px however
          few tiers there were, so a collapsed list left a block of dead space
          under the last row (QA). The height was there to reserve room for an
          EXPANDED tier — the body only turns on `overflow-y-auto` when one is
          open — and a cap serves that just as well: the panel hugs its content
          collapsed, grows as a tier opens, and scrolls once it reaches the
          cap. */}
      <DialogContent className="bg-surface-1 text-content-1 font-display border-line flex max-h-[610px] w-[495px] flex-col gap-0 overflow-hidden p-0 sm:max-h-[587px]">
        <DialogTitle className="sr-only">Configure Subscriptions</DialogTitle>
        <DialogDescription className="sr-only">
          Set up Subscriber, Fan, and VIP tiers — price, title, description, and
          promotional banner per level.
        </DialogDescription>

        {/* Header — back chevron / title / spacer. Same chrome
            as SeriesFormModal / ContactCardFormModal for layout
            consistency. */}
        <div className="border-raise/10 flex h-12 shrink-0 items-center justify-between border-b px-3">
          <button
            type="button"
            onClick={close}
            aria-label="Back"
            className="text-raise/70 hover:bg-raise/10 flex h-9 w-9 items-center justify-center rounded-full"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
          <span className="text-body">Configure Subscriptions</span>
          <span className="h-9 w-9" aria-hidden />
        </div>

        {/* Body — scrollable. Description header + tier list.
            1:1 with legacy `ConfigureSubscriptionPageHeader`. */}
        <div
          className={`relative flex min-h-0 flex-1 flex-col gap-4 px-4 py-5 ${
            openLevel != null ? 'overflow-y-auto' : 'overflow-y-hidden'
          }`}
        >
          <div className="text-content-2 text-sub font-normal leading-[1.4] tracking-normal">
            3 paid subscription levels help you monetize a wide audience with
            different levels of content
          </div>

          {/* 18+ self-certification. Paid tiers stay disabled until certified
              (see `useTierItems`); the checkbox removes itself once done. */}
          {!isAgeVerified ? <AgeVerificationCheckbox /> : null}

          <div className="text-content-2 border-raise/10 bg-raise/[0.05] text-sub rounded-lg border px-3 py-2 font-normal leading-[1.4] tracking-normal">
            Only pay when you earn - POP.STORE takes 15% to power your content
            with top-tier tools and zero upfront cost.
          </div>

          {loading && !subscriptions ? (
            <SubscriptionTierListSkeleton />
          ) : error ? (
            <div className="bg-danger-soft border-danger/40 text-sub text-danger rounded-lg border px-3 py-2 font-normal tracking-normal">
              Couldn't load your subscriptions. Pull to refresh or close and try
              again.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {items.map(item => {
                const level = item.subscription.level;
                return (
                  <SubscriptionTierRow
                    key={`${level}`}
                    item={item}
                    open={openLevel === level}
                    onOpenChange={next => {
                      setOpenLevel(next ? level : undefined);
                    }}
                    subscriptions={subscriptions}
                    sellerId={creatorId}
                    onSaved={handleTierSaved}
                    onError={handleTierError}
                    highlighted={
                      defaultSubscriptionLevel != null &&
                      level === defaultSubscriptionLevel
                    }
                  />
                );
              })}
            </div>
          )}

          {/* Inline toast — validation/save-error feedback. */}
          {toast ? (
            <div className="bg-danger-soft border-danger/40 text-sub text-danger pointer-events-none absolute inset-x-4 bottom-4 rounded-lg border px-3 py-2 text-center font-normal tracking-normal">
              {toast.msg}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionTierListSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="border-raise/10 bg-raise/[0.02] h-[60px] animate-pulse rounded-xl border"
        />
      ))}
    </div>
  );
}
